import React from 'react';
import type { LedgerGroupSummary, LedgerSummaryResponse } from '../../../services/aiControl/ledger';
import { money } from './ledgerModel';

/*
  DEVELOPER SPEND -- the issue console's real AI cost, from the same Ruby
  Ledger lines (params.kind = 'developer'): who spent it, on which profile and
  which issue. USD only: these runs charge nobody Ruby Tokens. Clicking a row
  narrows the ledger to it.
*/
const GroupTable: React.FC<{
  title: string;
  rows: LedgerGroupSummary[];
  emptyText: string;
  active?: string;
  onPick: (key: string) => void;
  testId: string;
}> = ({ title, rows, emptyText, active, onPick, testId }) => (
  <div className="min-w-0">
    <h3 className="text-[11px] uppercase tracking-wide text-stone-500 font-semibold py-1">{title}</h3>
    {rows.length === 0 ? (
      <p className="text-[12.5px] text-stone-500 py-2">{emptyText}</p>
    ) : (
      <table className="w-full text-[12.5px] text-stone-900" data-testid={testId}>
        <thead>
          <tr className="text-left text-[11px] text-stone-500">
            <th className="py-1 pr-3 font-medium">Name</th>
            <th className="py-1 px-3 font-medium text-right">Runs</th>
            <th className="py-1 pl-3 font-medium text-right">Spent</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-stone-100">
          {rows.map((g) => (
            <tr key={g.key} className={active === g.key ? 'bg-[#FFF1F3]' : undefined}>
              <td className="py-1 pr-3 max-w-0 w-2/3">
                <button
                  type="button"
                  onClick={() => onPick(g.key)}
                  className="w-full text-left truncate min-h-[44px] text-stone-900 hover:text-[#9F1239] font-medium"
                  title={g.key}
                >
                  {g.label}
                </button>
              </td>
              <td className="py-1 px-3 text-right tabular-nums text-stone-700">{g.runs}</td>
              <td className="py-1 pl-3 text-right tabular-nums text-stone-900 font-semibold">
                {money(g.vendorCostUsd)}
                {g.estimatedRuns > 0 && (
                  <span className="ml-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-800" title={`${g.estimatedRuns} run(s) still estimated`}>
                    {g.estimatedRuns} est.
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </div>
);

export const DeveloperSpend: React.FC<{
  summary: LedgerSummaryResponse | null;
  phrase: string;
  who: string;
  profileId: string;
  onWho: (key: string) => void;
  onProfile: (key: string) => void;
  onIssue: (key: string) => void;
}> = ({ summary, phrase, who, profileId, onWho, onProfile, onIssue }) => {
  const byAction = summary?.byAction ?? [];
  const spent = (id: string) => byAction.find((a) => a.actionId === id)?.vendorCostUsd ?? 0;
  const estimated = (summary?.byDeveloper ?? []).reduce((n, g) => n + g.estimatedRuns, 0);
  return (
    <section aria-label={`Developer spend for ${phrase}`} className="bg-white border border-stone-200 rounded-xl text-stone-900" data-testid="ledger-developer-spend">
      <div className="px-3 md:px-4 pt-3 pb-1">
        <h2 className="text-[13px] font-semibold text-stone-900">Developer spend · <span className="text-stone-600 font-normal">{phrase}</span></h2>
        <p className="text-[12px] text-stone-500">What Ruby cost us in the issue console — analysis and fixes — in dollars. Click a row to narrow the ledger.</p>
      </div>
      <dl className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-stone-100 border-y border-stone-100">
        {[
          ['Total spent', money(summary?.totals.vendorCostUsd ?? 0)],
          ['Analysis', money(spent('platform.issue_triage'))],
          ['Fixing', money(spent('platform.issue_fix'))],
          ['Still estimated', `${estimated} run${estimated === 1 ? '' : 's'}`],
        ].map(([label, value]) => (
          <div key={label} className="px-3 md:px-4 py-2.5 min-w-0">
            <dt className="text-[11px] text-stone-500 truncate">{label}</dt>
            <dd className="text-[15px] font-semibold tabular-nums truncate text-stone-900">{value}</dd>
          </div>
        ))}
      </dl>
      <div className="px-3 md:px-4 py-2 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <GroupTable title="By developer" rows={summary?.byDeveloper ?? []} emptyText="No developer runs." active={who} onPick={onWho} testId="ledger-by-developer" />
        <GroupTable title="By profile" rows={summary?.byProfile ?? []} emptyText="No profile recorded yet." active={profileId} onPick={onProfile} testId="ledger-by-profile" />
        <GroupTable title="Most expensive issues" rows={summary?.byIssue ?? []} emptyText="No issues yet." onPick={onIssue} testId="ledger-by-issue" />
      </div>
    </section>
  );
};
